<template>
  <HeadTitle
    title="Agregar compañias"
    description="Formulario de creacion de compañias de un cliente"
  />
  <Step class="mt-2" title="Seleccionar cliente" :step-number="1">
    <div v-if="initialLoading" class="flex flex-column gap-2 w-full">
      <Skeleton width="20%" height="3rem" />
    </div>
    <Dropdown
      v-else
      v-model="selectedClient"
      :optionLabel="clientLabel"
      :options="clients"
      @change="mountCompanies"
      placeholder="ej. codigo - nombre"
    >
      <template #option="slotProps">
        <div class="flex align-options-center">
          <div>{{ slotProps.option.code + ' - ' + slotProps.option.name }}</div>
        </div>
      </template>
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex align-items-center">
          <div>{{ slotProps.value.code }}-{{ slotProps.value.name }}</div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
    </Dropdown>
  </Step>
  <Divider></Divider>
  <Step :title="'Seleccionar compañias'" :step-number="2">
    <div class="step-body">
      <DataTable
        class="tabla"
        v-model:filters="filters"
        style="width: 90%"
        scrollable
        scrollHeight="600px"
        v-model:selection="selectedCompanies"
        :value="companies"
        dataKey="companyId"
        tableStyle="min-width: 50rem"
        :globalFilterFields="['code', 'name', 'companyId']"
        paginator
        :rows="5"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :currentPageReportTemplate="currentPageReportTemplate"
        :loading="companiesLoading"
      >
        <template #header>
          <div class="flex justify-content-end">
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText
                v-model="filters['global'].value"
                placeholder="Buscar"
              />
            </span>
          </div>
        </template>
        <template #empty>
          No se encontraron compañias. Favor de seleccionar un cliente con
          compañias disponibles.
        </template>
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        <Column field="code" header="Código" sortable></Column>
        <Column field="name" header="Nombre"></Column>
      </DataTable>
    </div>
  </Step>
  <ConfirmDialog></ConfirmDialog>
  <Footer>
    <Button
      label="Cancelar"
      severity="secondary"
      class="mr-2 text-sm font-light"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      severity="primary"
      @click="save"
      class="text-sm font-light"
      :disabled="!isValidToSave"
      :loading="saving"
    />
  </Footer>
</template>
<script setup lang="ts">
  import * as yup from 'yup'
  import { computed, onMounted, ref } from 'vue'
  import { FilterMatchMode } from 'primevue/api'
  import Step from '@/shared/components/Step.vue'
  import Footer from '@/shared/components/Footer.vue'
  import Client from '@/modules/clients/models/Client'
  import Company from '../models/Company'
  import {
    getClients,
    getCompanies,
  } from '@/modules/clients/services/clientService'
  import { useConfirm } from 'primevue/useconfirm'
  import Skeleton from 'primevue/skeleton'

  import { createCompanies } from '../services/companyService'
  import { useRouter } from 'vue-router'
  import useNotifications from '@/store/notification'
  import { useField } from 'vee-validate'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  const router = useRouter()

  const clientSchema = yup.object().required('El cliente es requerido')
  const { value: selectedClient } = useField<Client>(
    'selectedClient',
    clientSchema
  )

  const clients = ref()
  const selectedCompanies = ref()
  const companies = ref()
  const notificationStore = useNotifications()
  const confirm = useConfirm()

  const initialLoading = ref(false)
  const companiesLoading = ref(false)
  const saving = ref(false)

  const clientLabel = () => {
    return selectedClient.value?.code + ' - ' + selectedClient.value?.name
  }

  onMounted(async () => {
    initialLoading.value = true
    try {
      const response = await getClients('')
      clients.value = response.data.filter((c: Client) => c.code !== '4') //omiting Designer client, companies for this client must be created manually in database
    } finally {
      initialLoading.value = false
    }
  })

  const mountCompanies = async () => {
    companiesLoading.value = true
    try {
      const reponse = await getCompanies({
        integrationFields: selectedClient.value?.integrationFields,
        integrationTypeId: selectedClient.value?.integrationType.id,
        clientId: selectedClient.value?.id,
      })
      companies.value = reponse
    } finally {
      companiesLoading.value = false
    }
  }

  const save = async () => {
    confirm.require({
      message:
        'Al aceptar la sesión se cerrará automáticamente para efectuar cambios, ¿Deseas continuar?.',
      header: 'Confirmación',
      icon: 'pi pi-exclamation-circle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        saving.value = true
        try {
          const response = await createCompanies({
            clientId: selectedClient.value.id,
            companies: selectedCompanies.value.map((sc: Company) => ({
              clientId: selectedClient.value.id,
              ...sc,
            })),
          })
          if (response === 'OK') {
            notificationStore.addSuccesNotification({
              detail: `201`,
              severity: 'success',
              summary: `¡Compañia(s) creada(s) con exito!`,
            })
            router.push('/login')
          }
        } finally {
          saving.value = false
        }
      },
      reject: () => {},
    })
  }

  const isValidToSave = computed(
    () => selectedClient.value && selectedCompanies.value?.length > 0
  )

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${companies.value?.length || 0}`
  })
</script>
